import {Injectable, inject, DestroyRef} from '@angular/core';
import {combineLatest, Observable} from 'rxjs';
import {ServiceCardInfo} from '../../models/service/service-card-info';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {Solution} from '../../models/service/solution';
import {InstancesService} from '../api/instances.service';
import {BlueprintsService} from '../api/blueprints.service';
import {Instance} from '../../models/instance/instance';
import {DeployStatus} from '../../models/deploy/deploy-status';
import {ServiceStatus} from '../../models/service/service-status';
import {DeploymentClass} from '../../models/instance-class';
import {ServiceSetupProgress} from '../../models/service/service-setup-progress';
import {map} from 'rxjs/operators';
import {Blueprint} from 'src/app/models/blueprints/blueprint';
import {SolutionVersionsService} from './solution-versions.service';
import {SolutionVersion} from 'src/app/models/service/solution-version';

@Injectable({
  providedIn: 'root'
})
export class ServiceCardService  {
private destroyRef = inject(DestroyRef);

  constructor(private instancesService: InstancesService, private blueprintsService: BlueprintsService,
              private solutionVersionsService: SolutionVersionsService) {
    
  }

  getServiceCardInfos(services: Solution[], serviceVersions: Map<string, SolutionVersion[]>): Observable<ServiceCardInfo[]> {
    let instanceOperation = this.instancesService.getInstances();
    let blueprintsOperation = this.blueprintsService.getBlueprints();

    // TODO: retrieve only relevant blueprints and instances, when search filtering is present.
    const singleService = services.length === 1;
    if (singleService) {
      instanceOperation = this.instancesService.getInstancesForSolution(services[0].id);
      blueprintsOperation = this.blueprintsService.getBlueprintsForSolution(services[0].id);
    }

    return combineLatest([instanceOperation, blueprintsOperation])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(([instances, blueprints]) => this.toServiceCardInfos(services, blueprints, instances, serviceVersions)));
  }

  toServiceCardInfos(services: Solution[], blueprints: Blueprint[],
                     instances: Instance[], serviceVersions: Map<string, SolutionVersion[]>): ServiceCardInfo[] {
    const blueprintToInstances = this.configureBlueprintToInstances(instances);
    const idToService = this.configureIdsToServices(services);
    const serviceCardInfos = blueprints.map(blueprint => {
      const service = idToService.get(blueprint.solutionId);
      const blueprintInstances = blueprintToInstances.get(blueprint.id) || [];
      const versions = serviceVersions.get(service.id);
      const serviceVersionsInfo = this.solutionVersionsService.getSolutionVersionsInfo(
        service, versions ? versions : [], blueprintInstances);

      const serviceCardInfo = {
        blueprint,
        service,
        instances: blueprintInstances,
        serviceVersionsInfo
      } as ServiceCardInfo;
      serviceCardInfo.status = this.resolveServiceStatus(serviceCardInfo);
      return serviceCardInfo;
    });

    return this.sort(serviceCardInfos);
  }

  private configureBlueprintToInstances(instances): Map<string, Instance[]> {
    const blueprintToInstances = new Map<string, Instance[]>();
    instances.forEach(instance => {
      if (!blueprintToInstances.has(instance.blueprintId)) {
        blueprintToInstances.set(instance.blueprintId, []);
      }
      blueprintToInstances.get(instance.blueprintId)
        .push(instance);
    });
    return blueprintToInstances;
  }

  private configureIdsToServices(services): Map<string, Solution> {
    const idToService = new Map<string, Solution>();
    services.forEach(solution => {
      idToService.set(solution.id, solution);
    });
    return idToService;
  }

  private resolveServiceStatus(serviceCardInfo: ServiceCardInfo): ServiceStatus {
    const isLive = serviceCardInfo.instances.some(
      instance => instance.status === DeployStatus.DEPLOYED &&
        instance.deploymentClass === DeploymentClass.A);

    if (isLive) {
      return ServiceStatus.LIVE;
    }

    serviceCardInfo.currentStep = this.resolveNextPendingStep(serviceCardInfo);
    return ServiceStatus.IN_PROGRESS;

  }

  private resolveNextPendingStep(serviceCardInfo: ServiceCardInfo): ServiceSetupProgress {
    if (serviceCardInfo.serviceVersionsInfo?.currentVersion?.initialisationConfigurationInstructionId &&
      !serviceCardInfo.instances.length) {
      return ServiceSetupProgress.CREATE_CONFIGURATION_ENVIRONMENT;
    }

    if (!serviceCardInfo.blueprint.pathToLiveConfiguration?.length) {
      return ServiceSetupProgress.DEFINE_PTL_WORKFLOW;
    }

    return ServiceSetupProgress.DEPLOY_PTL;
  }

  /**
   * Sorts the blueprints so that the sandboxes go first and then
   * the rest of blueprints
   *
   * @param - service card infos
   */
  private sort(serviceCardInfos: ServiceCardInfo[]): ServiceCardInfo[] {
    const demoBlueprints = this.getDemoBlueprints(serviceCardInfos);

    if (demoBlueprints.length > 0) {
      serviceCardInfos.forEach((serviceCardInfo, i) => {
        if (demoBlueprints.includes(serviceCardInfo.blueprint.id)) {
          serviceCardInfos.splice(i, 1);
          serviceCardInfos.unshift(serviceCardInfo);
        }
      });
    }

    return serviceCardInfos;
  }

  /**
   * Finds and prepares the demo blueprints
   *
   * @param - service card infos
   * @returns - the demo blueprints
   */
  private getDemoBlueprints(serviceCardInfos: ServiceCardInfo[]): string[] {
    const demoBlueprints = [];

    serviceCardInfos.forEach(cardInfo => {
      if (cardInfo.blueprint.demo) {
        demoBlueprints.push(cardInfo.blueprint.id);
      }
    });

    return demoBlueprints;
  }
}
